// src/pages/TeacherDashboard.js
import React, { useState, useEffect }            from 'react';
import { useNavigate }                           from 'react-router-dom';
import { signOut, onAuthStateChanged }           from 'firebase/auth';
import {
  doc,
  onSnapshot,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { auth, db }                              from '../firebase';

import CreateAttendance      from '../components/CreateAttendance';
import CreateTest            from '../components/CreateTest';
import EditAttendanceList    from '../components/EditAttendanceList';
import EditTestList          from '../components/EditTestList';
import Layout from '../components/Layout';

export default function TeacherDashboard() {
  const [view, setView]           = useState('');           // '', 'batches', 'batchDetail', 'attendance', 'test', 'editChoice', 'editAttendance', 'editTest'
  const [batches, setBatches]     = useState([]);
  const [selectedBatch, setBatch] = useState(null);
  const [userName, setUserName]   = useState('');
  const navigate                  = useNavigate();

  // Fetch and display teacher’s name
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, user => {
      if (!user) {
        navigate('/login');
        return;
      }
      const unsubSnap = onSnapshot(
        doc(db, 'users', user.uid),
        snap => setUserName(snap.data()?.name || ''),
        err => console.error('Name fetch error:', err)
      );
      return () => unsubSnap();
    });
    return () => unsubAuth();
  }, [navigate]);

  // Load this teacher’s batches
  const loadBatches = async () => {
    const q = query(
      collection(db, 'batches'),
      where('teacher_id', '==', auth.currentUser.uid)
    );
    const snap = await getDocs(q);
    setBatches(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  const handleMyBatches = async () => {
    setView('batches');
    setBatch(null);
    await loadBatches();
  };

  const openBatch = batch => {
    setBatch(batch);
    setView('batchDetail');
  };

  // Back‐to‐batchDetail helper
  const backToBatchDetail = () => {
    setView('batchDetail');
  };

  return (
    <Layout>
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Teacher Dashboard</h1>
        <div className="flex items-center gap-4">
          {userName && <span className="text-gray-800">Hello, {userName}</span>}
          <br />
          <button
            onClick={handleMyBatches}
            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            My Batches
          </button>
          <button
            onClick={handleLogout}
           className="px-3 py-1 bg-[#FF6B00] text-white rounded-md hover:bg-[#e65c00]"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div>
        {/* List of Batches */}
        {view === 'batches' && (
          <div>
            <h2 className="text-xl mb-4">My Batches</h2>
            {batches.length === 0 ? (
              <p className="text-gray-600">No batches assigned.</p>
            ) : (
              <ul className="space-y-2">
                {batches.map(b => (
                  <li key={b.id}>
                    <button
                      onClick={() => openBatch(b)}
                       className="w-full text-left px-4 py-2 border rounded-md hover:bg-gray-100"
                    >
                      {b.batch_name} ({b.class} — {b.subject})
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Batch Detail */}
        {view === 'batchDetail' && selectedBatch && (
          <div>
            <h2 className="text-xl mb-4">Batch: {selectedBatch.batch_name}</h2>
            <div className="flex gap-4 mb-6">
              <button
                onClick={() => setView('attendance')}
                className="px-4 py-2 bg-[#FF6B00] text-white rounded-md hover:bg-[#e65c00]"
              >
                Create Attendance
              </button>
              <button
                onClick={() => setView('test')}
                className="px-4 py-2 bg-[#FF6B00] text-white rounded-md hover:bg-[#e65c00]"
              >
                Create Test
              </button>
              <button
                onClick={() => setView('editChoice')}
                className="px-4 py-2 bg-[#FF6B00] text-white rounded-md hover:bg-[#e65c00]"
              >
                Edit Details
              </button>
              <button
                onClick={handleMyBatches}
               className="px-4 py-2 bg-[#FF6B00] text-white rounded-md hover:bg-[#e65c00]"
              >
                Back to Batches
              </button>
            </div>
          </div>
        )}

        {/* Edit Choice */}
        {view === 'editChoice' && selectedBatch && (
          <div>
            <h2 className="text-xl mb-4">Edit for {selectedBatch.batch_name}</h2>
            <div className="flex gap-4 mb-6">
              <button
                onClick={() => setView('editAttendance')}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Edit Attendance
              </button>
              <button
                onClick={() => setView('editTest')}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Edit Tests
              </button>
              <button
                onClick={backToBatchDetail}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Back
              </button>
            </div>
          </div>
        )}

        {/* Create / Edit Forms */}
        {view === 'attendance' && selectedBatch && (
          <CreateAttendance
            batchId={selectedBatch.id}
            onBack={backToBatchDetail}
          />
        )}
        {view === 'test' && selectedBatch && (
          <CreateTest
            batchId={selectedBatch.id}
            onBack={backToBatchDetail}
          />
        )}
        {view === 'editAttendance' && selectedBatch && (
          <EditAttendanceList
            batchId={selectedBatch.id}
            onBack={backToBatchDetail}
          />
        )}
        {view === 'editTest' && selectedBatch && (
          <EditTestList
            batchId={selectedBatch.id}
            onBack={backToBatchDetail}
          />
        )}

        {/* Initial Prompt */}
        {!view && (
          <p className="text-gray-600">
            Click “My Batches” to load your assigned batches.
          </p>
        )}
      </div>
    </div>
    </Layout>
  );
}
